import { Injectable, inject, signal, computed } from "@angular/core";
import { Product } from "../../shared/models/product.model";
import { ScanIframeService } from "./scan-iframe.service";
import {
  ProductRemovedEvent,
  ProductQuantityChangedEvent,
  ProductsClearedEvent,
} from "../models/scan-iframe.contract";
import { ProductResponse } from "../../shared/models/product-response.model";

export interface ScannedProduct {
  product: Product; // Product data from database
  scanMode: "native" | "camera";
  timestamp: Date;
  barcode: string; // Barcode that was scanned to get this product
  quantity: number; // Quantity of this product scanned
}

@Injectable({
  providedIn: "root",
})
export class ScannedProductsService {
  private scanIframeService = inject(ScanIframeService);

  private scannedProducts = signal<Record<string, ScannedProduct>>({});
  private previousQuantities = new Map<string, number>();

  readonly products = computed(() => this.scannedProducts());
  readonly hasProducts = computed(
    () => Object.keys(this.scannedProducts()).length > 0,
  );
  readonly productCount = computed(
    () => Object.keys(this.scannedProducts()).length,
  );
  readonly productsAsArray = computed(() =>
    Object.values(this.scannedProducts()),
  );
  readonly lastScannedProduct = computed(() => {
    const products = Object.values(this.scannedProducts());
    if (products.length === 0) return null;
    return products.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    )[0];
  });

  /**
   * Add a product to the scanned products collection
   * @param product - The product to add
   * @param barcode - Optional barcode data that was scanned to get this product
   * @param scanMode - The scanning mode used ('native' or 'camera')
   * @returns The added scanned product
   */
  addProduct(
    productResponse: ProductResponse,
    barcode: string,
    scanMode: "native" | "camera" = "camera",
  ): ScannedProduct {
    const existingProduct =
      this.scannedProducts()[productResponse.upc?.toString()];

    if (existingProduct) {
      // If product already exists, increment quantity
      return this.incrementQuantity(productResponse.upc?.toString());
    }

    const scannedProduct: ScannedProduct = {
      product: productResponse,
      scanMode,
      timestamp: new Date(),
      barcode,
      quantity: 1,
    };

    this.scannedProducts.update((prev) => ({
      ...prev,
      [productResponse.upc?.toString()]: scannedProduct,
    }));

    // Store initial quantity
    this.previousQuantities.set(
      productResponse.upc?.toString(),
      scannedProduct.quantity,
    );

    // Note: Notifications are handled by BarcodeScanningService to avoid duplicates

    return scannedProduct;
  }

  /**
   * Increment quantity for a product by SKU
   * @param sku - The SKU of the product to increment
   * @returns The updated scanned product
   */
  incrementQuantity(sku: string): ScannedProduct {
    const previousQuantity = this.previousQuantities.get(sku) || 1;

    this.scannedProducts.update((prev) => {
      const product = prev[sku];
      if (!product) return prev;

      return {
        ...prev,
        [sku]: {
          ...product,
          quantity: (product.quantity || 1) + 1,
          timestamp: new Date(), // Update timestamp to reflect most recent scan
        },
      };
    });

    const updatedProduct = this.scannedProducts()[sku];
    const newQuantity = updatedProduct.quantity || 1;

    // Update stored quantity
    this.previousQuantities.set(sku, newQuantity);

    // Notify iframe about quantity change
    this.notifyQuantityChanged(
      updatedProduct,
      previousQuantity,
      newQuantity,
      "increment",
    );

    return updatedProduct;
  }

  /**
   * Decrement quantity for a product by SKU
   * @param sku - The SKU of the product to decrement
   * @returns The updated scanned product or null if removed
   */
  decrementQuantity(sku: string): ScannedProduct | null {
    const product = this.scannedProducts()[sku];
    if (!product) return null;

    this.scannedProducts.update((prev) => {
      const newQuantity = (product.quantity || 1) - 1;

      if (newQuantity <= 0) {
        // Remove product if quantity reaches 0
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [sku]: _, ...rest } = prev;
        this.previousQuantities.delete(sku);
        return rest;
      }

      return {
        ...prev,
        [sku]: {
          ...product,
          quantity: newQuantity,
        },
      };
    });

    const updatedProduct = this.scannedProducts()[sku];

    if (!updatedProduct) {
      // Product was removed
      // Note: Notifications are handled by BarcodeScanningService to avoid duplicates
      return null;
    }

    const newQuantity = updatedProduct.quantity || 1;
    this.previousQuantities.set(sku, newQuantity);

    // Note: Notifications are handled by BarcodeScanningService to avoid duplicates

    return updatedProduct;
  }

  /**
   * Update quantity for a product by SKU
   * @param sku - The SKU of the product to update
   * @param quantity - The new quantity
   * @returns The updated scanned product or null if removed
   */
  updateQuantity(sku: string, quantity: number): ScannedProduct | null {
    const product = this.scannedProducts()[sku];
    if (!product) return null;

    // const previousQuantity = product.quantity || 1;

    if (quantity <= 0) {
      this.removeProduct(sku);
      return null;
    }

    this.scannedProducts.update((prev) => {
      return {
        ...prev,
        [sku]: {
          ...product,
          quantity,
        },
      };
    });

    const updatedProduct = this.scannedProducts()[sku];
    if (!updatedProduct) return null;

    this.previousQuantities.set(sku, quantity);

    // Notify iframe about quantity change
    // this.notifyQuantityChanged(
    //   updatedProduct,
    //   previousQuantity,
    //   quantity,
    //   "update",
    // );

    return updatedProduct;
  }

  /**
   * Remove a product by SKU (completely remove regardless of quantity)
   * @param sku - The SKU of the product to remove
   */
  removeProduct(sku: string): void {
    // const product = this.scannedProducts()[sku];

    this.scannedProducts.update((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sku]: _, ...rest } = prev;
      return rest;
    });

    this.previousQuantities.delete(sku);

    // Notify iframe about product removal
    // if (product) {
    //   this.notifyProductRemoved(product);
    // }
  }

  /**
   * Remove the last scanned product
   */
  removeLastProduct(): void {
    const lastProduct = this.lastScannedProduct();
    if (lastProduct) {
      this.removeProduct(lastProduct.product.upc.toString());
    }
  }

  /**
   * Clear all scanned products
   */
  clearAll(): void {
    // const clearedCount = Object.keys(this.scannedProducts()).length;
    this.scannedProducts.set({});
    this.previousQuantities.clear();

    // Notify iframe about products cleared
    // this.notifyProductsCleared(clearedCount);
  }

  /**
   * Get a product by SKU
   * @param sku - The SKU to search for
   * @returns The scanned product if found, undefined otherwise
   */
  getProductBySku(sku: string): ScannedProduct | undefined {
    return this.scannedProducts()[sku];
  }

  /**
   * Check if a product exists by SKU
   * @param sku - The SKU to check
   * @returns True if the product exists, false otherwise
   */
  hasProduct(sku: string): boolean {
    return sku in this.scannedProducts();
  }

  /**
   * Get products scanned in a specific mode
   * @param scanMode - The scan mode to filter by
   * @returns Array of products scanned in the specified mode
   */
  getProductsByMode(scanMode: "native" | "camera"): ScannedProduct[] {
    return Object.values(this.scannedProducts()).filter(
      (product) => product.scanMode === scanMode,
    );
  }

  // Private methods for iframe notifications

  private notifyProductRemoved(scannedProduct: ScannedProduct): void {
    const productRemovedEvent: ProductRemovedEvent = {
      product: scannedProduct.product,
      barcode: scannedProduct.barcode,
      timestamp: scannedProduct.timestamp.toISOString(),
      scanMode: scannedProduct.scanMode,
      quantity: scannedProduct.quantity,
    };
    this.scanIframeService.sendProductRemoved(productRemovedEvent);
  }

  private notifyQuantityChanged(
    scannedProduct: ScannedProduct,
    previousQuantity: number,
    newQuantity: number,
    changeType: "increment" | "decrement" | "update",
  ): void {
    const quantityChangedEvent: ProductQuantityChangedEvent = {
      product: scannedProduct.product,
      barcode: scannedProduct.barcode,
      timestamp: scannedProduct.timestamp.toISOString(),
      scanMode: scannedProduct.scanMode,
      quantity: scannedProduct.quantity,
      quantityChange: {
        previousQuantity,
        newQuantity,
        changeType,
      },
    };
    this.scanIframeService.sendProductQuantityChanged(quantityChangedEvent);
  }

  private notifyProductsCleared(count: number): void {
    const productsClearedEvent: ProductsClearedEvent = {
      clearedCount: count,
      timestamp: new Date().toISOString(),
    };
    this.scanIframeService.sendProductsCleared(productsClearedEvent);
  }
}
